import { useState, useRef, useLayoutEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, ArrowRight, Check, MessageCircle, Loader2, BadgeDollarSign, GraduationCap } from 'lucide-react'
import { chatService } from '@/lib/api/services/chat.service'
import { freelancerService } from '@/lib/api/services/freelancer.service'
import { useProfile } from '@/lib/hooks/use-profile'
import { useOwnClientProfile } from '@/lib/hooks/use-own-client-profile'
import { useClientProfileCompletion } from '@/lib/hooks/use-client-profile-completion'
import { toast } from 'sonner'
import { StartChatConfirmationDialog } from '@/components/chat/start-chat-confirmation-dialog'

interface FreelancerCardProps {
  freelancer: {
    id?: string | null
    user_id?: string | null // Clerk user ID (optional, will be fetched if not provided)
    display_name?: string | null
    profile_picture_url?: string | null
    headline?: string | null
    location?: string | null
    hourly_rate_cents?: number | null
    rate_range_min_cents?: number | null
    rate_range_max_cents?: number | null
    skills?: unknown // Allow Json type from database
    [key: string]: unknown // Allow additional properties
  }
  showSecondCheckIcon?: boolean
  clickable?: boolean
  onCardClick?: () => void
}

const getInitials = (name: string | null) => {
  if (!name) return '?'
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

const formatRateRange = (minCents: number | null, maxCents: number | null) => {
  const min = minCents ? Math.round(minCents / 100) : null
  const max = maxCents ? Math.round(maxCents / 100) : null

  if (min && max) {
    if (min === max) {
      return `$${min}/hr`
    }
    return `$${min} - $${max}/hr`
  }
  if (min) {
    return `$${min}+/hr`
  }
  if (max) {
    return `up to $${max}/hr`
  }
  return null
}

export function FreelancerCard({
  freelancer,
  showSecondCheckIcon = false,
  clickable: _clickable = false,
  onCardClick: _onCardClick,
}: FreelancerCardProps) {
  const navigate = useNavigate()
  const { profile } = useProfile()
  const [isStartingChat, setIsStartingChat] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const allSkills = useMemo(
    () => (Array.isArray(freelancer.skills) ? (freelancer.skills as string[]) : []),
    [freelancer.skills]
  )
  const skillsContainerRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState<number>(allSkills.length)

  // Reset visible count when skills change
  useLayoutEffect(() => {
    setVisibleCount(allSkills.length)
  }, [allSkills.length])

  // Reduce visible skills if they overflow the container
  useLayoutEffect(() => {
    const container = skillsContainerRef.current
    if (!container || allSkills.length === 0) return

    const overflows = container.scrollHeight > container.clientHeight
    if (overflows && visibleCount > 1) {
      setVisibleCount((prev) => prev - 1)
    }
  }, [allSkills, visibleCount])

  const visibleSkills = allSkills.slice(0, visibleCount)
  const remainingSkillsCount = Math.max(0, allSkills.length - visibleCount)
  const initials = getInitials(freelancer.display_name ?? null)
  const rate = formatRateRange(
    freelancer.rate_range_min_cents ?? null,
    freelancer.rate_range_max_cents ?? null
  )

  // Check if user is a client (only clients can start chats from freelancer cards)
  const isClient = profile?.is_client ?? false
  
  // Get client profile completion status for clients
  const { clientProfile } = useOwnClientProfile()
  const profileCompletion = useClientProfileCompletion(clientProfile)

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!freelancer.id) return
    navigate(`/genius/${freelancer.id}`)
  }

  const handleStartChat = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!freelancer.id) return

    // Only clients can start chats
    if (!isClient) {
      toast.error('Only clients can start conversations with freelancers')
      return
    }

    // Check if client profile is complete
    if (!profileCompletion.isComplete) {
      toast.error('Please complete your profile before starting a conversation')
      navigate('/client/profile')
      return
    }

    // Show confirmation dialog
    setShowConfirmDialog(true)
  }

  const confirmStartChat = async () => {
    if (!freelancer.id) return

    setShowConfirmDialog(false)
    setIsStartingChat(true)
    try {
      // Get the user_id from the freelancer profile
      // The freelancer.id is the profile ID, we need to fetch the profile to get user_id
      let freelancerUserId: string
      
      // Check if user_id is already available in the freelancer object
      if (freelancer.user_id && typeof freelancer.user_id === 'string') {
        freelancerUserId = freelancer.user_id
      } else {
        // Fetch the profile to get the user_id
        const profile = await freelancerService.getProfile(freelancer.id)
        if (!profile.user_id) {
          throw new Error('Freelancer user ID not found')
        }
        freelancerUserId = profile.user_id
      }

      // Create or find existing conversation using user_id (not profile ID)
      const conversation = await chatService.createOrFindConversation(freelancerUserId)
      
      // Navigate to chat page with conversation ID
      navigate(`/client/chat?conversationId=${conversation.id}`)
    } catch (error) {
      console.error('Failed to start chat:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to start chat. Please try again.'
      toast.error(errorMessage)
    } finally {
      setIsStartingChat(false)
    }
  }

  return (
    <div
      className="rounded-2xl sm:rounded-[32px] border border-slate-200 p-4 sm:p-5 lg:p-6 overflow-hidden flex flex-col"
      style={{
        background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
        height: '354px', // Fixed height for the entire card
      }}
    >
      {/* Header - Fixed Height: 100px to accommodate wrapping */}
      <div className="flex-shrink-0 h-20 mb-2">
        <div className="flex gap-3 sm:gap-4 min-w-0 h-full">
          <div className="relative flex-shrink-0">
            <div
              className="w-12 h-12 sm:w-[65px] sm:h-[65px] rounded-2xl sm:rounded-[32px] flex items-center justify-center overflow-hidden"
              style={{
                background: freelancer.profile_picture_url
                  ? 'transparent'
                  : 'linear-gradient(135deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                boxShadow: '0px 4px 12px 0px rgba(70, 105, 237, 0.2)',
              }}
            >
              {freelancer.profile_picture_url ? (
                <img
                  src={freelancer.profile_picture_url}
                  alt={freelancer.display_name || 'Genius'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-black text-lg sm:text-2xl">{initials}</span>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0 flex flex-col justify-start overflow-visible">
            <div className="flex items-center gap-2 mb-1.5 min-w-0">
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 truncate flex-1 min-w-0 overflow-hidden" style={{ minHeight: '32px', lineHeight: '32px' }}>
                {freelancer.display_name || ''}
              </h3>
              {showSecondCheckIcon && (
                <div className="w-5 h-5 sm:w-[22px] sm:h-[22px] rounded-md bg-[#4669ed] flex items-center justify-center flex-shrink-0">
                  <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                </div>
              )}
              {Boolean(freelancer.certification_interested) && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold flex items-center gap-1 flex-shrink-0">
                  <GraduationCap className="w-3 h-3" />
                  Cert Candidate
                </span>
              )}
            </div>

            {/* Flexible container for location and rate - allows wrapping without cut-off */}
            <div className="flex flex-wrap items-start gap-2 text-sm sm:text-[15px] text-slate-700 min-w-0">
              {freelancer.location && (
                <span className="flex items-center gap-1 min-w-0 flex-shrink-0">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">{freelancer.location}</span>
                </span>
              )}
              {rate && (
                <span className="flex items-center gap-1 flex-shrink-0">
                  <BadgeDollarSign className="w-4 h-4 flex-shrink-0" />
                  {rate}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body - Fixed Height: 202px (reduced to compensate for header increase) */}
      <div className="flex-shrink-0 flex flex-col">
        {/* Headline section - Fixed height: 72px */}
        <div className="h-14 mb-2 flex items-start">
          {freelancer.headline && (
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed line-clamp-2 overflow-hidden">
              {freelancer.headline}
            </p>
          )}
        </div>

        {/* Skills section - Fixed height: 96px, dynamically shows fewer skills if overflow */}
        <div
          ref={skillsContainerRef}
          className="h-24 flex items-start overflow-hidden"
        >
          <div className="flex flex-wrap gap-1.5 sm:gap-2 overflow-hidden w-full" style={{ alignContent: 'flex-start' }}>
            {visibleSkills.map((skill, idx) => (
              <span
                key={idx}
                className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full sm:rounded-[20px] bg-[#d1e0fc] text-[#2b42a3] font-semibold text-xs sm:text-[13px] flex-shrink-0"
              >
                {skill}
              </span>
            ))}
            {remainingSkillsCount > 0 && (
              <span className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full sm:rounded-[20px] bg-slate-200 text-slate-600 font-semibold text-xs sm:text-[13px] flex-shrink-0">
                +{remainingSkillsCount} more
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Footer - Fixed Height: 70px */}
      <div 
        className="flex items-center justify-center border-t-2 border-slate-200 flex-shrink-0 h-16 pt-3 gap-4"
      >
        {isClient && (
        <button
          onClick={handleStartChat}
          disabled={isStartingChat}
          className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-[22px] py-2 sm:py-3 rounded-full border-2 font-semibold text-xs sm:text-sm transition-all flex-shrink-0 whitespace-nowrap ${
            !profileCompletion.isComplete
              ? 'bg-orange-600 border-orange-600 text-white hover:bg-orange-700 hover:border-orange-700 shadow-md'
              : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
          title="Start chat"
        >
            {isStartingChat ? (
              <>
                <Loader2 className="animate-spin h-5 w-5 text-slate-400" />
                Starting...
              </>
            ) : (
              <>
                <MessageCircle className="w-4 h-4 flex-shrink-0" />
                Start Chat
              </>
            )}
          </button>
        )}
        <button
          onClick={handleButtonClick}
          className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-[22px] py-2 sm:py-3 rounded-full border-2 font-semibold text-xs sm:text-sm transition-all bg-[#4669ed] border-[#4669ed] text-white hover:bg-[#4669ed]/90 hover:border-[#4669ed]/90 flex-shrink-0 whitespace-nowrap"
        >
          View Profile
          <ArrowRight className="w-4 h-4 flex-shrink-0" />
        </button>
      </div>

      {/* Confirmation Dialog */}
      <StartChatConfirmationDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        freelancerName={freelancer.display_name}
        onConfirm={confirmStartChat}
        isStartingChat={isStartingChat}
      />
    </div>
  )
}

